import { useMemo } from 'react';
import type React from 'react';
import type { JSONContent } from '@tiptap/core';
import {
  isRichTextContentEmpty,
  useTheme,
  type BasicContentBlockA,
  type BasicContentBlockB,
  type BasicContentImageSize,
  type BasicContentLayout,
  type BasicContentTitleLevel,
} from '@course-authoring/shared-types';

import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { getContentBlockHeadingTypography } from './styles/titleTypography';
import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  safeGenerateRichTextHTML,
} from './richTextRenderer';
import { RichTextContent } from './RichTextContent';

type BasicContentBlock = BasicContentBlockA | BasicContentBlockB;

function resolveLayout(block: BasicContentBlock): BasicContentLayout {
  if (block.layout) {
    return block.layout;
  }

  return block.type === 'basic-content-b' ? 'image-right' : 'image-top';
}

function resolveImageSize(block: BasicContentBlock): BasicContentImageSize {
  return block.imageSize ?? 'medium';
}

function resolveTitleLevel(block: BasicContentBlock): BasicContentTitleLevel {
  return block.titleLevel ?? 'h1';
}

function splitLegacyParagraphs(body: string): string[] {
  return body
    .split(/\n\n+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

const bodyResetStyles = createRichTextContentResetStyles('basic-content-body-rich', '0.6em');
const MOBILE_BLOCK_WIDTH = '90%';
const DESKTOP_SPLIT_BLOCK_WIDTH = '80%';
const DESKTOP_IMAGE_RIGHT_GAP = '3.125%';
const DESKTOP_IMAGE_TOP_TEXT_WIDTH = '60%';
const DESKTOP_IMAGE_TOP_IMAGE_WIDTH = '70%';
const DESKTOP_LEFT_INSET = '10%';

const IMAGE_TOP_WIDTH: Record<BasicContentImageSize, string> = {
  large:  '100%',
  medium: '55%',
  small:  '40%',
};

// Image Left/Right: 8-column grid — image columns / text columns
const SPLIT_IMAGE_COLUMNS: Record<BasicContentImageSize, number> = {
  large: 4,
  medium: 3,
  small: 2,
};

function getSplitColumnWidths(imageSize: BasicContentImageSize): { image: string; text: string } {
  const imageCols = SPLIT_IMAGE_COLUMNS[imageSize] ?? 3;
  const textCols = 8 - imageCols;
  const image = `calc(${imageCols} / 8 * (100% - ${DESKTOP_IMAGE_RIGHT_GAP}))`;
  const text = `calc(${textCols} / 8 * (100% - ${DESKTOP_IMAGE_RIGHT_GAP}))`;
  return { image, text };
}

interface BasicContentBlockRendererProps {
  block: BasicContentBlock;
  className?: string;
}

export function BasicContentBlockRenderer({
  block,
  className,
}: BasicContentBlockRendererProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  // NA-205: body copy holds its 20px desktop size through the tablet mid-range and
  // only drops to 16px on narrow phones (≤576px); headings/layout still shift at 768px.
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const layout = resolveLayout(block);
  const imageSize = resolveImageSize(block);
  const titleLevel = resolveTitleLevel(block);
  const richTextExtensions = useMemo(() => createRichTextRendererExtensions(), []);

  const isLegacyBody = typeof block.body === 'string';
  const legacyBodyText = typeof block.body === 'string' ? block.body : '';
  const richBodyContent = typeof block.body === 'string' ? null : block.body;
  const legacyParagraphs = useMemo(
    () => (isLegacyBody ? splitLegacyParagraphs(legacyBodyText) : []),
    [isLegacyBody, legacyBodyText]
  );
  const bodyHtml = useMemo(() => {
    if (!richBodyContent) {
      return '';
    }
    return safeGenerateRichTextHTML(richBodyContent as JSONContent, richTextExtensions);
  }, [richBodyContent, richTextExtensions]);

  const hasImage = layout !== 'no-image' && Boolean(block.imageUrl);
  const hasTitle = block.title.trim().length > 0;
  const hasBody = isLegacyBody
    ? legacyParagraphs.length > 0
    : !isRichTextContentEmpty(richBodyContent as JSONContent);
  const hasTextContent = hasTitle || hasBody;
  const isSideLayout = layout === 'image-right' || layout === 'image-left';
  const isSideLayoutDesktop = isSideLayout && !isMobile;
  const isSplitDesktop = isSideLayoutDesktop && hasImage && hasTextContent;
  const useLeftInset = !isMobile && (layout === 'image-top' || layout === 'no-image');

  const splitWidths = getSplitColumnWidths(imageSize);
  const textMaxWidth = isMobile
    ? MOBILE_BLOCK_WIDTH
    : isSideLayoutDesktop
      ? (isSplitDesktop ? splitWidths.text : DESKTOP_IMAGE_TOP_TEXT_WIDTH)
      : DESKTOP_IMAGE_TOP_TEXT_WIDTH;
  const imageMaxWidth = isMobile
    ? MOBILE_BLOCK_WIDTH
    : isSideLayoutDesktop
      ? splitWidths.image
      : IMAGE_TOP_WIDTH[imageSize];
  const imageContainerMaxWidth = isMobile
    ? MOBILE_BLOCK_WIDTH
    : isSideLayoutDesktop
      ? splitWidths.image
      : DESKTOP_IMAGE_TOP_IMAGE_WIDTH;
  const imageMaxHeight = imageMaxWidth;

  const headingStyle = getContentBlockHeadingTypography(tokens, titleLevel, {
    isMobile,
    isSmallMobile,
  });

  const styles: Record<string, React.CSSProperties> = {
    section: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    content: {
      width: isSplitDesktop ? DESKTOP_SPLIT_BLOCK_WIDTH : '100%',
      display: 'flex',
      flexDirection: isSplitDesktop ? 'row' : 'column',
      alignItems: isSplitDesktop ? 'stretch' : useLeftInset && hasImage ? 'flex-start' : 'center',
      justifyContent: 'flex-start',
      gap: isSplitDesktop ? DESKTOP_IMAGE_RIGHT_GAP : tokens.spacing.lg,
      margin: isSplitDesktop ? '0 auto' : 0,
    },
    textContainer: {
      width: textMaxWidth,
      maxWidth: textMaxWidth,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      marginLeft: !isSplitDesktop && useLeftInset && hasImage ? DESKTOP_LEFT_INSET : 0,
    },
    imageContainer: {
      width: imageMaxWidth,
      maxWidth: imageContainerMaxWidth,
      marginLeft: !isSplitDesktop && useLeftInset ? DESKTOP_LEFT_INSET : 0,
      backgroundColor: 'transparent',
      flexShrink: 0,
    },
    imageInner: {
      position: 'relative',
      display: 'flex',
      width: '100%',
      alignItems: 'flex-start',
      justifyContent: layout === 'image-left' ? 'flex-end' : 'flex-start',
    },
    imageSurface: {
      position: 'relative',
      display: 'block',
      maxWidth: '100%',
      width: '100%',
    },
    image: {
      display: 'block',
      borderRadius: tokens.radius.md,
      ...(isSideLayout
        ? {
            width: '100%',
            maxHeight: imageMaxHeight,
          }
        : {
            width: '100%',
            height: 'auto',
          }),
    },
    title: {
      margin: 0,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: headingStyle.size,
      fontWeight: headingStyle.weight,
      lineHeight: headingStyle.lineHeight,
    },
    bodyText: {
      margin: 0,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: isSmallMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size,
      fontWeight: isSmallMobile ? tokens.typography.mobile.body.weight : tokens.typography.body.weight,
      lineHeight: isSmallMobile
        ? tokens.typography.mobile.body.lineHeight
        : tokens.typography.body.lineHeight,
    },
    richBody: {
      margin: 0,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: isSmallMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size,
      fontWeight: isSmallMobile ? tokens.typography.mobile.body.weight : tokens.typography.body.weight,
      lineHeight: isSmallMobile
        ? tokens.typography.mobile.body.lineHeight
        : tokens.typography.body.lineHeight,
    },
  };

  const HeadingTag = titleLevel;

  const textNode = hasTextContent ? (
    <div style={styles.textContainer}>
      {hasTitle ? <HeadingTag style={styles.title}>{block.title}</HeadingTag> : null}
      {hasBody ? (
        isLegacyBody ? (
          legacyParagraphs.map((paragraph, index) => (
            <p key={`${block.id}-body-${index}`} style={styles.bodyText}>
              {paragraph}
            </p>
          ))
        ) : (
          <>
            <style>{bodyResetStyles}</style>
            <RichTextContent
              className="basic-content-body-rich"
              style={styles.richBody}
              html={bodyHtml}
            />
          </>
        )
      ) : null}
    </div>
  ) : null;

  const imageNode = hasImage ? (
    <div style={styles.imageContainer}>
      <div style={styles.imageInner}>
        <div style={styles.imageSurface}>
          <img src={block.imageUrl} alt={block.imageAlt} style={styles.image} loading="lazy" />
        </div>
      </div>
    </div>
  ) : null;

  return (
    <section style={styles.section} className={className}>
      <div style={styles.content}>
        {isSplitDesktop && layout === 'image-right' ? (
          <>
            {textNode}
            {imageNode}
          </>
        ) : (
          <>
            {imageNode}
            {textNode}
          </>
        )}
      </div>
    </section>
  );
}

BasicContentBlockRenderer.displayName = 'BasicContentBlockRenderer';
